import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GreenroomState } from '../../../../stores/types/types';
import { MediaItem } from '../../../../stores/reducers/media';
import { IMG_TYPES } from '../../../../types/constants';
import { VirtualBg } from '../../../../stores/types/content';
import { setModalState } from '../../../../stores/actions/modal';
import { MODAL_TEMPLATES } from '../../../../stores/reducers/modal';
import { SelectedOverlay } from '../../../right-panel/user-menus/effects/elements';
import * as keylisteners from '../../../../utils/keyboard-handler';
import { ReactComponent as TellyHeart } from '../../../../images/telly-heart.svg';
import * as l from './elements';
import useAddBackground from '../../../../hooks/virtual-backgrounds/use-add-background';

const FileTypeToColor = new Map<string, string>([
	['jpg', '#00A3E9'],
	['jpeg', '#00A3E9'],
	['png', '#00A3E9'],
	['gif', '#00A3E9'],

	['ppt', '#DB6905'],

	['mp4', '#8162FF'],
	['hls', '#8162FF'],
	['mov', '#8162FF'],

	['doc', '#4D88FD'],
]);

export default function MediaLibraryContainer() {
	const dispatch = useDispatch();
	const { media } = useSelector((s: GreenroomState) => s.MediaReducer);

	const close = () => {
		dispatch(setModalState({
			modalTemplate: MODAL_TEMPLATES.EMPTY,
			modalOpen: false,
			modalData: {},
		}));
	};

	const { createVbgFromMedia } = useAddBackground(close);

	const imageMedia = useMemo(
		() => media?.filter((m: MediaItem) => IMG_TYPES.includes(m.type) && m.type !== 'gif'),
		[media],
	);

	const [
		selectedMediaItem,
		setSelectedMediaItem,
	] = React.useState<VirtualBg>();

	const myBackgrounds: VirtualBg[] = useMemo(
		() => imageMedia.reduce((curr: Array<VirtualBg>, m: MediaItem) => {
			curr.push({
				url: m.media_data.image_array[0],
				thumbnail: m.thumbnail_url,
				blur: 0,
				name: m.custom_name ?? m.original_name ?? '',
				fileType: m.type,
				uniqueId: `media_${m.id.toString()}`,
			});
			return curr;
		}, []),
		[imageMedia],
	);

	const save = () => {
		if (!selectedMediaItem) return;
		createVbgFromMedia(selectedMediaItem);
	};

	useEffect(() => {
		keylisteners.addListener(keylisteners.KEYS.KEY_ESCAPE, close);
		keylisteners.addListener(keylisteners.KEYS.KEY_ENTER, save);
		return () => {
			keylisteners.removeListener(keylisteners.KEYS.KEY_ESCAPE, close);
			keylisteners.removeListener(keylisteners.KEYS.KEY_ESCAPE, save);
		};
	}, []);

	const badge = (type: string) => (
		<l.FileBadge
			className='flex-center bl-font-l2'
			style={{ background: FileTypeToColor.get(type) ?? '#000' }}
		>
			{type}
		</l.FileBadge>
	);

	const selectItem = (item: VirtualBg) => {
		setSelectedMediaItem(item);
	};

	return (
		<l.ModalBackground className='content-modal'>
			<l.GradientBackdrop hidden={myBackgrounds.length !== 0}/>
			<l.Shadow hidden={myBackgrounds.length === 0} />
			<l.LibraryHeader>
				<div className='bl-font-h4' style={{ color: 'white' }}>Select Files</div>
			</l.LibraryHeader>
			<l.CardContainerContainer>
				<l.CardContainer>
					{myBackgrounds.map((bg, i) => (
						<l.Card key={`${i}-${bg.url}`}>
							<div className='scaled-container' onClick={() => selectItem(bg)}>
								<l.ImageContainer>
									<img src={bg.thumbnail ?? bg.url ?? ''} alt=''/>
									<SelectedOverlay
										hidden={selectedMediaItem?.url !== bg.url}
										style={{ border: '2px solid white', background: 'rgba(0, 0, 0, 0.2)' }}
									/>
								</l.ImageContainer>
								{bg.fileType ? badge(bg.fileType) : null}
							</div>
							<l.MediaTitle className={`bl-font-s3 text-gray-${selectedMediaItem?.url === bg.url ? '100' : '200'}`}>
								{bg.name ?? ''}
							</l.MediaTitle>
						</l.Card>
					))}
				</l.CardContainer>
			</l.CardContainerContainer>
			<l.ShadowBottom hidden={myBackgrounds.length === 0}/>
			<l.EmptyState hidden={myBackgrounds.length !== 0}>
				<TellyHeart />
				<div className='bl-font-h4 text-green'>No media in your greenroom</div>
				<div className='bl-font-p2 text-gray-400'>Upload to your media tab first</div>
			</l.EmptyState>
			<l.LibraryFooter>
				<div className='flex'>
					<button
						className='button-greenroom-basic margin-right-medium background-grayscale-600 padding-button-medium-wide border-radius-round text-gray-200 button-background-shading'
						onClick={() => {
							dispatch(setModalState({
								modalTemplate: MODAL_TEMPLATES.EMPTY,
								modalOpen: false,
								modalData: {},
							}));
						}}
					>Cancel</button>
					<button
						onClick={save}
						className={`
							button-background-shading
							background-green
							text-dark
							padding-button-medium-wide
							border-radius-round
							button-background-shading
							${(myBackgrounds.length === 0 || !selectedMediaItem) ? 'disabled' : ''}
						`}
					>Add</button>
				</div>
			</l.LibraryFooter>
		</l.ModalBackground>
	);
}
